import { money, minutes, type Money, type Minutes, type ResourceId } from '../domain/branded.js';
import type { Project } from '../domain/project.js';
import { EntityNotFoundError, InvalidArgumentError } from '../domain/errors.js';

export interface BudgetResourceTotal {
  readonly resourceId: ResourceId;
  readonly work: Minutes;
  readonly cost: Money;
}

export interface BudgetResult {
  readonly totalWork: Minutes;
  readonly totalCost: Money;
  readonly byResource: readonly BudgetResourceTotal[];
}

/** Validate budget allocations without mutation. Budget assignments never enter scheduling or cost/EVM. */
export function validateBudgetAssignments(project: Project): void {
  const seenResourceIds = new Set<string>();
  const seenAssignmentIds = new Set<string>();
  const resources = new Map(project.resources.map((resource) => [resource.id as string, resource]));

  for (const assignment of project.budgetAssignments ?? []) {
    const resourceId = assignment.resourceId as string;
    if (seenAssignmentIds.has(assignment.id)) {
      throw new InvalidArgumentError(`duplicate budget assignment id: ${assignment.id}`);
    }
    seenAssignmentIds.add(assignment.id);

    if (seenResourceIds.has(resourceId)) {
      throw new InvalidArgumentError(`duplicate budget resource: ${resourceId}`);
    }
    seenResourceIds.add(resourceId);

    const resource = resources.get(resourceId);
    if (resource === undefined) throw new EntityNotFoundError('RESOURCE_NOT_FOUND', resourceId);
    if (!resource.isBudget) throw new InvalidArgumentError(`resource ${resourceId} is not a budget resource`);

    const work = assignment.budgetWork;
    const cost = assignment.budgetCost;
    if ((work === undefined) === (cost === undefined)) {
      throw new InvalidArgumentError(`budget assignment ${assignment.id} must contain exactly one of budgetWork/budgetCost`);
    }
    const value = work ?? cost;
    if (value === undefined || !Number.isFinite(value as number) || (value as number) < 0) {
      throw new InvalidArgumentError(`budget assignment ${assignment.id} has a negative or non-finite value`);
    }
    if ((resource.type === 'Cost') !== (cost !== undefined)) {
      throw new InvalidArgumentError(`budget assignment ${assignment.id} does not match resource type`);
    }
  }
}

export function computeBudget(project: Project): BudgetResult {
  validateBudgetAssignments(project);
  let totalWork = 0;
  let totalCost = 0;
  const byResource = (project.budgetAssignments ?? []).map((assignment): BudgetResourceTotal => {
    const work = (assignment.budgetWork ?? minutes(0)) as number;
    const cost = (assignment.budgetCost ?? money(0)) as number;
    totalWork += work;
    totalCost += cost;
    return { resourceId: assignment.resourceId, work: minutes(work), cost: money(cost) };
  });
  return { totalWork: minutes(totalWork), totalCost: money(totalCost), byResource };
}
